import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from "@/components/ui/command";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentUserPermissions } from "@/hooks/useAdminPermissions";
import { useSystemSettings } from "@/hooks/useSystemSettings";
import {
  LayoutDashboard,
  Bell,
  FileText,
  ListChecks,
  Receipt,
  Shield,
  Users,
  Settings,
} from "lucide-react";

type CommandDef = {
  label: string;
  description?: string;
  icon?: React.ElementType;
  to: string;
  shortcut?: string;
  group: string;
  adminModule?: string;
};

const isMacLike = () => {
  if (typeof navigator === "undefined") return false;
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
};

const CommandMenu: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();
  const { data: permissions = {} } = useCurrentUserPermissions();
  const { getSettingValue } = useSystemSettings();
  const [open, setOpen] = React.useState(false);
  const configuredKey =
    (String(getSettingValue("command_palette_shortcut") ?? "k") || "k").toLowerCase();

  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTypingTarget =
        !!target &&
        (target.closest("input, textarea, select") ||
          target.isContentEditable ||
          target.closest("[contenteditable='true']"));
      if (isTypingTarget) return;

      const isShortcutKey = e.key.toLowerCase() === configuredKey;
      if (!isShortcutKey) return;
      const isCmdK = isMacLike() ? e.metaKey : e.ctrlKey;
      if (!isCmdK) return;
      e.preventDefault();
      setOpen((v) => !v);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [configuredKey]);

  const go = (to: string) => {
    setOpen(false);
    if (to === location.pathname + location.search) return;
    navigate(to);
  };

  const role = profile?.role ?? "unknown";

  const commands: CommandDef[] = React.useMemo(() => {
    const common: CommandDef[] = [
      {
        label: "Notifications",
        description: "View recent updates",
        icon: Bell,
        to: "/notifications",
        shortcut: "N",
        group: "Common",
      },
    ];

    if (role === "admin" || role === "super_admin") {
      const isSuperAdmin = role === "super_admin";
      const isModuleEnabled = (moduleName: string): boolean => {
        if (isSuperAdmin) return true;
        return (permissions as Record<string, boolean | undefined>)[moduleName] !== false;
      };

      const adminCommands: CommandDef[] = [
        ...common,
        {
          label: "Admin Dashboard",
          icon: LayoutDashboard,
          to: "/admin-dashboard",
          shortcut: "D",
          group: "Admin",
        },
        {
          label: "Manage Documents",
          icon: FileText,
          to: "/admin-dashboard?tab=manage-documents",
          group: "Admin",
          adminModule: "client_management_list",
        },
        {
          label: "Client Management",
          icon: Users,
          to: "/admin-dashboard?tab=clients",
          group: "Admin",
          adminModule: "client_management_list",
        },
        {
          label: "Task Management",
          icon: ListChecks,
          to: "/admin-dashboard?tab=tasks",
          group: "Admin",
          adminModule: "task_management_overview",
        },
        {
          label: "Billing",
          icon: Receipt,
          to: "/admin-dashboard?tab=billing",
          group: "Admin",
          adminModule: "billing_invoices_list",
        },
        {
          label: "Support Logs",
          icon: Shield,
          to: "/admin-dashboard?tab=support-logs",
          group: "Admin",
        },
        {
          label: "Settings",
          icon: Settings,
          to: "/admin-dashboard?tab=settings",
          group: "Admin",
          adminModule: "system_settings",
        },
      ];

      return adminCommands.filter((cmd) => {
        if (!cmd.adminModule) return true;
        return isModuleEnabled(cmd.adminModule);
      });
    }

    if (role === "staff") {
      return [
        ...common,
        {
          label: "Staff Dashboard",
          icon: LayoutDashboard,
          to: "/staff-dashboard",
          group: "Staff",
        },
        {
          label: "Tasks",
          icon: ListChecks,
          to: "/tasks",
          group: "Staff",
        },
        {
          label: "Documents",
          icon: FileText,
          to: "/documents",
          group: "Staff",
        },
      ];
    }

    if (role === "client") {
      return [
        ...common,
        {
          label: "Client Dashboard",
          icon: LayoutDashboard,
          to: "/client-dashboard",
          group: "Client",
        },
        {
          label: "My Documents",
          icon: FileText,
          to: "/client-documents",
          group: "Client",
        },
        {
          label: "My Tasks",
          icon: ListChecks,
          to: "/client-tasks",
          group: "Client",
        },
        {
          label: "My Invoices",
          icon: Receipt,
          to: "/client-invoices",
          group: "Client",
        },
        {
          label: "Profile",
          icon: Settings,
          to: "/profile",
          group: "Client",
        },
      ];
    }

    return common;
  }, [role, permissions]);

  const groups = React.useMemo(() => {
    const map = new Map<string, CommandDef[]>();
    for (const cmd of commands) {
      const list = map.get(cmd.group) ?? [];
      list.push(cmd);
      map.set(cmd.group, list);
    }
    return Array.from(map.entries());
  }, [commands]);

  const upperKey = configuredKey.toUpperCase();
  const shortcutLabel = isMacLike() ? `⌘${upperKey}` : `Ctrl ${upperKey}`;

  return (
    <CommandDialog open={open} onOpenChange={setOpen}>
      <CommandInput placeholder={`Search… (${shortcutLabel})`} />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {groups.map(([group, list], index) => (
          <React.Fragment key={group}>
            {index > 0 ? <CommandSeparator /> : null}
            <CommandGroup heading={group}>
              {list.map((cmd) => {
                const Icon = cmd.icon;
                return (
                  <CommandItem
                    key={`${group}:${cmd.label}`}
                    value={`${cmd.label} ${cmd.description ?? ""}`.trim()}
                    onSelect={() => go(cmd.to)}
                  >
                    {Icon ? <Icon className="mr-2 h-4 w-4" /> : null}
                    <div className="flex flex-col">
                      <span>{cmd.label}</span>
                      {cmd.description ? (
                        <span className="text-xs text-muted-foreground">{cmd.description}</span>
                      ) : null}
                    </div>
                    {cmd.shortcut ? <CommandShortcut>{cmd.shortcut}</CommandShortcut> : null}
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </React.Fragment>
        ))}
      </CommandList>
    </CommandDialog>
  );
};

export default CommandMenu;
